import { useEffect, useRef } from 'react';
import { Icon } from '@/features/common/Icon';
import type { ActiveFilterTagsProps } from '@/features/publicsite/types/ActiveFilterTagsProps';
import { listingFilterPanel } from '@/i18n/dictionaries/listingFilterPanel';
import { useTranslation } from '@/i18n/useTranslation';

/**
 * The filters a listing is narrowed by, under its filter bar: one tag per list filter, each with a
 * ✕ that takes off that filter alone, then "Șterge filtrele" for all of them — the bar's only way
 * to clear everything, so it is there too when only the search or the period is set, which have no
 * tag of their own. Nothing while nothing is set.
 *
 * The button pressed goes away with its filter, and focus with it would fall back to the top of the
 * document. It moves instead to the tag that took the removed one's place — the one after, or the
 * one before when the last went — and to the search field (`fallbackFocusId`) once none is left.
 */
export function ActiveFilterTags({
  tags,
  canClear,
  onClear,
  fallbackFocusId,
}: ActiveFilterTagsProps) {
  const { t } = useTranslation(listingFilterPanel);
  const listRef = useRef<HTMLUListElement>(null);
  const removedAt = useRef<number | null>(null);
  const names = tags.map((tag) => tag.name).join(' ');

  useEffect(() => {
    const index = removedAt.current;
    if (index === null) return;
    removedAt.current = null;
    const remaining = listRef.current?.querySelectorAll('button') ?? [];
    const target = remaining[Math.min(index, remaining.length - 1)];
    (target ?? document.getElementById(fallbackFocusId))?.focus();
  }, [names, canClear, fallbackFocusId]);

  if (tags.length === 0 && !canClear) return null;

  return (
    <div className="filter-tags">
      {tags.length > 0 && (
        <ul className="filter-tags__list" aria-label={t('activeFilters')} ref={listRef}>
          {tags.map((tag, i) => (
            <li key={tag.name} className="status-tag status-tag--brand is-subtle">
              {tag.label}
              <button
                type="button"
                className="filter-tags__remove"
                aria-label={t('removeFilter', { label: tag.label })}
                onClick={() => {
                  removedAt.current = i;
                  tag.onRemove();
                }}
              >
                <Icon name="cross-large" className="filter-tags__icon" />
              </button>
            </li>
          ))}
        </ul>
      )}
      <button
        type="button"
        className="btn btn--text-primary"
        onClick={() => {
          removedAt.current = 0;
          onClear();
        }}
      >
        {t('clearFilters')}
      </button>
    </div>
  );
}
